// Allergen multi-select grid: 12 toggles (English primary, Latin secondary).
// Multi-select with a clear selected state. Default = none selected. onChange
// delivers the ordered list of selected allergen indices.
//
// The header (title + Select all / Clear) lives in the enclosing sheet/panel
// (see ui/sheet.ts) so the same grid works identically inside the mobile bottom
// sheet and the desktop side panel; selectAll()/clearAll() are public so that
// header can drive them.

import type { Allergen } from "../manifest";
import { canonicalCode } from "../regionRegistry";

export interface AllergenPanelOptions {
  allergens: Allergen[];
  onChange: (selected: number[]) => void;
  /** Optional: notify before a (first-time) selection that may upload. */
  onBeforeSelect?: (index: number) => Promise<void>;
  /**
   * Optional (multi-region): return a short region tag for a taxon (e.g. "EU"
   * "NA" "Asia") so a chip can indicate which regions carry it. Returns the
   * tags in display order; an empty/omitted result shows no badge.
   */
  regionTags?: (index: number) => string[];
}

export class AllergenPanel {
  /** The grid element only (no header). */
  readonly el: HTMLElement;
  private buttons: HTMLButtonElement[] = [];
  private selected = new Set<number>();
  private order: number[] = [];
  private opts: AllergenPanelOptions;

  constructor(opts: AllergenPanelOptions) {
    this.opts = opts;

    this.el = document.createElement("div");
    this.el.className = "allergen-grid";
    this.el.id = "allergen-grid";

    this.opts.allergens.forEach((a, i) => {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "allergen-chip";
      btn.setAttribute("aria-pressed", "false");
      const tags = this.opts.regionTags?.(i) ?? [];
      // Only badge taxa that are NOT in every region (region-specific ones),
      // so the common shared taxa (birch, grass, ragweed...) stay uncluttered.
      // Inline-styled (self-contained within app/src; no global CSS dependency).
      const badge =
        tags.length > 0 && tags.length < 3
          ? `<span class="chip-regions" aria-label="Available in ${tags.join(", ")}" ` +
            `style="display:flex;gap:3px;margin-top:3px;">` +
            tags
              .map(
                (t) =>
                  `<span class="chip-region" style="font-size:9px;font-weight:700;` +
                  `letter-spacing:.04em;padding:1px 4px;border-radius:4px;` +
                  `background:rgba(255,255,255,.14);color:#cfe2f5;">${t}</span>`,
              )
              .join("") +
            `</span>`
          : "";
      btn.innerHTML =
        `<span class="chip-en">${a.english}</span>` +
        `<span class="chip-la">${a.latin}</span>` +
        badge;
      btn.addEventListener("click", () => void this.toggle(i));
      this.buttons.push(btn);
      this.el.appendChild(btn);
    });
  }

  private async toggle(i: number) {
    if (this.selected.has(i)) {
      this.selected.delete(i);
      this.order = this.order.filter((x) => x !== i);
    } else {
      if (this.opts.onBeforeSelect) {
        this.buttons[i].classList.add("loading");
        await this.opts.onBeforeSelect(i);
        this.buttons[i].classList.remove("loading");
      }
      this.selected.add(i);
      this.order.push(i);
    }
    this.syncUI();
    this.opts.onChange(this.order.slice());
  }

  /** Select every allergen sequentially (deterministic order; one onChange/each). */
  async selectAll(): Promise<void> {
    for (let i = 0; i < this.opts.allergens.length; i++) {
      if (!this.selected.has(i)) {
        if (this.opts.onBeforeSelect) {
          this.buttons[i].classList.add("loading");
          await this.opts.onBeforeSelect(i);
          this.buttons[i].classList.remove("loading");
        }
        this.selected.add(i);
        this.order.push(i);
        this.syncUI();
        this.opts.onChange(this.order.slice());
      }
    }
  }

  clearAll(): void {
    this.selected.clear();
    this.order = [];
    this.syncUI();
    this.opts.onChange([]);
  }

  // Await each toggle sequentially so the resulting selection ORDER matches the
  // requested `indices` deterministically (and exactly one onChange fires per
  // index). toggle() awaits its own onBeforeSelect decode; firing them
  // un-awaited would let decodes resolve out of order and make the ordered
  // selection (and the e2e handle) non-deterministic. (red-team m1)
  async setSelected(indices: number[]): Promise<void> {
    this.clearAll();
    for (const i of indices) await this.toggle(i);
  }

  /** Restore a selection from stable allergen CODES (e.g. from a shared URL). */
  async setSelectedByCodes(codes: string[]): Promise<void> {
    // Match by CANONICAL code so a shared URL using an aliased raw code (e.g. a
    // legacy "PINU" link) still resolves to the merged entry ("PINA"/Pine).
    const byCode = new Map(
      this.opts.allergens.map((a, i) => [canonicalCode(a.code), i]),
    );
    const idxs: number[] = [];
    for (const c of codes) {
      const i = byCode.get(canonicalCode(c));
      if (i !== undefined && !idxs.includes(i)) idxs.push(i);
    }
    await this.setSelected(idxs);
  }

  /** Current selection as stable allergen codes, in selection order. */
  getSelectedCodes(): string[] {
    return this.order.map((i) => this.opts.allergens[i].code);
  }

  get selectedCount(): number {
    return this.selected.size;
  }

  private syncUI() {
    this.buttons.forEach((b, i) => {
      const on = this.selected.has(i);
      b.classList.toggle("selected", on);
      b.setAttribute("aria-pressed", on ? "true" : "false");
    });
  }
}
